import React from 'react';
import {useSelector} from 'react-redux';

import {Constants} from '../../constants';
import {WriteLanternOptions} from '../../lib/ble2/v2/PeakDevice/IPeakDevice';
import {useAppState} from '../../lib/hooks/useAppState';
import {
  connectedPeakSelector,
  currentDeviceSWRevisionSelector,
  currentDeviceSelector,
  updateDevice,
} from '../../lib/redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {useAppDispatch} from '../../lib/redux/useAppDispatch';
import {isMoodLightId} from '../../lib/types';
import {meetsMinimumFirmware} from '../../lib/utils';
import {createContainer} from '../unstated-next';
import {IndexedQueue} from './indexed-queue';

type LightingItem = {key: string; value: WriteLanternOptions};

const useUnitLighting = () => {
  const peak = useSelector(connectedPeakSelector);
  const currentDevice = useSelector(currentDeviceSelector);
  const dispatch = useAppDispatch();
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);
  const deviceSWRevision = useSelector(currentDeviceSWRevisionSelector);
  const appState = useAppState();

  const canUseMoodLights = meetsMinimumFirmware(
    deviceSWRevision,
    Constants.MINIMUM_FIRMWARE_VERSION.MOOD_LIGHTING,
  );

  const queue = React.useRef(new IndexedQueue<LightingItem>());

  const setLanternIsOn = React.useCallback(
    async (value: boolean) => {
      if (!peak || !currentDevice?.id) return;

      if (!value) {
        await peak.stopLantern().then(() => {
          dispatch(
            updateDevice({
              id: currentDevice.id,
              lantern: {
                ...currentDevice.lantern,
                on: false,
              },
            }),
          );
        });

        return;
      }

      await peak.startLantern().then(() => {
        dispatch(
          updateDevice({
            id: currentDevice.id,
            lantern: {
              ...currentDevice.lantern,
              on: true,
            },
          }),
        );
      });
    },
    [peak, currentDevice?.id, currentDevice?.lantern],
  );

  const setLanternLight = React.useCallback(
    async (options?: WriteLanternOptions) => {
      if (!peak || !currentDevice?.id) return;

      if (options && isMoodLightId(options) && !canUseMoodLights) return;

      if (!options) {
        await peak.stopLantern().then(() => {
          dispatch(
            updateDevice({
              id: currentDevice.id,
              lantern: {
                ...currentDevice.lantern,
                on: false,
              },
            }),
          );
        });

        return;
      }

      await peak
        .writeLantern(options)
        .then(() => peak.startLantern())
        .then(() => {
          dispatch(
            updateDevice({
              id: currentDevice?.id,
              lantern: {
                on: true,
                light: options,
              },
            }),
          );
        });
    },
    [peak, currentDevice?.id, currentDevice?.lantern, canUseMoodLights],
  );

  const setPreviewLight = React.useCallback(
    async (options?: WriteLanternOptions) => {
      if (!peak || !currentDevice?.id) return;

      if (options && isMoodLightId(options) && !canUseMoodLights) return;

      if (!options) {
        await peak.stopLantern().then(() => {
          const light =
            currentDevice.lantern.light &&
            isMoodLightId(currentDevice.lantern.light)
              ? moodLights[currentDevice.lantern.light.id]
              : currentDevice.lantern.light;

          if (!light) return;

          return peak.writeLantern(light);
        });

        return;
      }

      // TODO: Use preview if available
      await peak.writeLantern(options).then(() => peak.startLantern());
    },
    [
      peak,
      canUseMoodLights,
      currentDevice?.lantern.light,
      currentDevice?.id,
      moodLights,
    ],
  );

  const queuePreviewLight = React.useCallback(
    async (key: string, options?: WriteLanternOptions) => {
      if (!options) return;

      if (!peak) return;

      await setPreviewLight(options);
      queue.current.enqueue({key, value: options});
    },
    [peak, setPreviewLight],
  );

  const updateEnqueuedLight = React.useCallback(
    async (key: string, options?: WriteLanternOptions) => {
      if (!options) return;

      if (!peak) return;

      if (!queue.current.has(key)) return;

      await setPreviewLight(options);
      queue.current.enqueue({key, value: options});
    },
    [peak, setPreviewLight],
  );

  const dequeuePreviewLight = React.useCallback(
    async (key: string) => {
      if (!peak) return;

      const options = queue.current.at(-2);

      const getLightOptions = () => {
        if (options) return options.value;

        if (!currentDevice) return;

        if (!currentDevice.lantern.on || !currentDevice.lantern.light) return;

        if (isMoodLightId(currentDevice.lantern.light))
          return moodLights[currentDevice.lantern.light.id];

        return currentDevice.lantern.light;
      };

      const light = getLightOptions();

      await setPreviewLight(light);

      queue.current.dequeue(key);
    },
    [peak, currentDevice?.lantern, moodLights, setPreviewLight],
  );

  React.useEffect(() => {
    const isLanternRunning = queue.current.length > 0;

    if (appState === 'active' && isLanternRunning) {
      setLanternIsOn(true);

      return;
    }

    if (isLanternRunning) {
      setLanternIsOn(false);
    }
  }, [appState]);

  return React.useMemo(
    () => ({
      setLanternLight,
      setLanternIsOn,
      setPreviewLight,
      queuePreviewLight,
      dequeuePreviewLight,
      updateEnqueuedLight,
    }),
    [
      setLanternLight,
      setLanternIsOn,
      setPreviewLight,
      queuePreviewLight,
      dequeuePreviewLight,
      updateEnqueuedLight,
    ],
  );
};

export const UnitLighting = createContainer(useUnitLighting);
